import React, { useState, useEffect } from 'react';
import { UxGlobalMetrics, UxTooltip } from 'botbit-ui-components'
import { getGlobalMetricsKpisByCompanyId, getGlobalMetricsKpisByStoreId } from '../../../api/dashboardV2Api';
import { GoogleKpiIcon, RayKpiIcon, GreenCircleIcon, YellowCircleIcon, RedCircleIcon } from './globalMetricsIcons';
import { formatNumber } from '../../../commons/utils/formats';
import translate from '../../../config/translations/translate'
import './GlobalMetrics.scss';


const GlobalMetrics = ({ companyId, storeId, period, t }) => {
    const [metrics, setMetrics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const tooltipContent = React.useMemo(() => (type) => {
        return (
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'space-between', justifyContent: 'space-between', gap: '10px', width: '100%'}}>
                <div className='circle-icon-and-text-container'>
                    <span className='circle-icon-and-text-span'><RedCircleIcon/> {t('metrics.trafficLight.values.bad')}</span>
                    <span className='circle-icon-and-text-span'><YellowCircleIcon/> {t('metrics.trafficLight.values.regular')}</span>
                    <span className='circle-icon-and-text-span'><GreenCircleIcon/> {t('metrics.trafficLight.values.good')}</span>
                </div>
                <div className='text-container'>
                    <span className='text-span'> {type === 'engagement' ? t('metrics.trafficLight.percentages.engagement.bad') : t('metrics.trafficLight.percentages.sentimentAndNps.bad')}</span>  
                    <span className='text-span'> {type === 'engagement' ? t('metrics.trafficLight.percentages.engagement.regular') : t('metrics.trafficLight.percentages.sentimentAndNps.regular')}</span>
                    <span className='text-span'> {type === 'engagement' ? t('metrics.trafficLight.percentages.engagement.good') : t('metrics.trafficLight.percentages.sentimentAndNps.good')}</span>
                </div>
            </div>
        )
    }, [t]);

    const getTrafficLightIcon = React.useMemo(() => (value, type) => {
        if (value === null || value === undefined) return null;

        if (type === 'engagement') {
            if (value < 3) return <UxTooltip tooltipTitle={<RedCircleIcon/>} tooltipContent={tooltipContent('engagement')} />;
            if (value >= 3 && value < 5) return <UxTooltip tooltipTitle={<YellowCircleIcon />} tooltipContent={tooltipContent('engagement')} />;
            if (value >= 5) return <UxTooltip tooltipTitle={<GreenCircleIcon />} tooltipContent={tooltipContent('engagement')} />;
        } else if (type === 'sentiment' || type === 'nps') {
            if (value < 65) return <UxTooltip tooltipTitle={<RedCircleIcon />} tooltipContent={tooltipContent('sentiment')} />;
            if (value >= 65 && value < 85) return <UxTooltip tooltipTitle={<YellowCircleIcon />} tooltipContent={tooltipContent('sentiment')} />;
            if (value >= 85) return <UxTooltip tooltipTitle={<GreenCircleIcon />} tooltipContent={tooltipContent('sentiment')} />;
        }
        return null;
    }, [tooltipContent]);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const response = companyId ? await getGlobalMetricsKpisByCompanyId(companyId, period) :
                    storeId ? await getGlobalMetricsKpisByStoreId(storeId, period) : 
                    null;
                    
                if(response === null) {
                    setMetrics([]);
                    setLoading(false);
                    throw new Error('Either companyId or storeId must be provided')
                }

                if (!isMounted) return;
                
                const responseData = response.data.data;
                const noDataMessage = t("noData");

                // Extraigo los valores para evitar optional chaining en JSX
                const engagementValue = responseData.engagementRateCompared && responseData.engagementRateCompared.value !== null && responseData.engagementRateCompared.value !== 0
                    ? responseData.engagementRateCompared.value
                    : null;
                const npsValue = responseData.npsScoreCompared && responseData.npsScoreCompared.value !== null && responseData.positiveSentimentCompared.value !== 0
                    ? responseData.npsScoreCompared.value
                    : null;
                const sentimentValue = responseData.positiveSentimentCompared && responseData.positiveSentimentCompared.value !== null && responseData.positiveSentimentCompared.value !== 0
                    ? responseData.positiveSentimentCompared.value
                    : null;

                const transformedMetrics = [
                    {
                        title: t('metrics.googleMapsRanking.title'),
                        score: responseData.averageRankingGoogleCompared && responseData.averageRankingGoogleCompared.value !== null && responseData.averageRankingGoogleCompared.value !== 0 ? 
                               responseData.averageRankingGoogleCompared.value.toFixed(2) : noDataMessage,
                        // variation: response.data.averageRankingGoogleCompared && response.data.averageRankingGoogleCompared.variationPercentage !== null ? 
                        //           response.data.averageRankingGoogleCompared.variationPercentage : null,
                        icon: <GoogleKpiIcon />,
                        tooltipContent: (
                            <div>
                                <p className='mb-0'>{t('metrics.googleMapsRanking.tooltip')}</p>
                            </div>
                        ),
                    },
                    {
                        title: t('metrics.reviewsScore.title'),
                        score: responseData.averageRatingCompared && responseData.averageRatingCompared.value !== null && responseData.averageRatingCompared.value !== 0 ? 
                               formatNumber(responseData.averageRatingCompared.value) : noDataMessage,
                        // variation: response.data.averageRatingCompared && response.data.averageRatingCompared.variationPercentage !== null ? 
                        //           response.data.averageRatingCompared.variationPercentage : null,
                        icon: <GoogleKpiIcon />,
                        tooltipContent: (
                            <div>
                                <p className='mb-0'>{t('metrics.reviewsScore.tooltip')}</p>
                            </div>
                        ),
                    },
                    {
                        title: t('metrics.engagementRate.title'),     
                        score: (
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '10px'}}>
                                {engagementValue !== null ? (engagementValue.toFixed(2) + '%') : noDataMessage}
                                {getTrafficLightIcon(engagementValue, 'engagement')}
                            </div>
                        ),
                        icon: <GoogleKpiIcon />,
                        tooltipContent: (
                            <div>
                                <p className='mb-0'>{t('metrics.engagementRate.tooltip')}</p>
                            </div>
                        ),
                    },
                    {
                        title: t('metrics.npsScore.title'),
                        score: (
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '10px'}}>
                                {npsValue !== null ? (npsValue.toFixed(2) + '%') : noDataMessage}
                                {getTrafficLightIcon(npsValue, 'nps')}
                            </div>
                        ),
                        icon: <RayKpiIcon />,
                        tooltipContent: (
                            <div>
                                <p className='mb-0'>{t('metrics.npsScore.tooltip')}</p>
                            </div>
                        ),
                    },
                    {
                        title: t('metrics.positiveSentiment.title'),
                        score: (
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '10px'}}>
                                {sentimentValue !== null ? (sentimentValue.toFixed(2) + '%') : noDataMessage}
                                {getTrafficLightIcon(sentimentValue, 'sentiment')}
                            </div>
                        ),
                        icon: <RayKpiIcon />,
                        tooltipContent: (
                            <div>
                                <p className='mb-0'>{t('metrics.positiveSentiment.tooltip')}</p>
                            </div>
                        ),
                    },
                ];
                setMetrics(transformedMetrics);
            } catch (err) {
                if (!isMounted) return;
                console.error('Error fetching global metrics:', err);
                setError(err);
                setMetrics([]);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();
        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [companyId, storeId, period, t]);

    const renderedMetrics = React.useMemo(() => (
        <div className='global-metrics-container w-100'>
            <UxGlobalMetrics 
                metrics={loading ? [] : metrics}
                loading={loading}
                error={error ? true : false}
            />
        </div>
    ), [metrics, loading, error]);

    return renderedMetrics;
};

GlobalMetrics.translations = {
    metrics: {
        googleMapsRanking: {
            title: {
                es: "Ranking Promedio Google Maps",
                en: "Average Google Maps Ranking"
            },
            tooltip: {
                es: "Mide la posición promedio de todos tus locales, cuando los consumidores buscan a 1 km de radio de cada sucursal en Google Maps.",
                en: "Measures the average position of all your locations when consumers search within a 1 km radius of each branch in Google Maps."
            }
        },
        reviewsScore: {
            title: {
                es: "Promedio Score Reviews",
                en: "Average Review Score"
            },
            tooltip: {
                es: "Mide el promedio de estrellas de los Reviews de todos los locales en el período de tiempo seleccionado",
                en: "Measures the average star rating of Reviews for all locations in the selected time period"
            }
        },
        engagementRate: {
            title: {
                es: "Engagement Rate Google Business Profile",
                en: "Google Business Profile Engagement Rate"
            },
            tooltip: {
                es: "Mide en porcentaje, cuanto interactúan con los perfiles de Google Maps los consumidores que los visitan",
                en: "Measures the percentage of how much consumers who visit interact with Google Maps profiles"
            }, 
        },
        npsScore: {
            title: {
                es: "Satisfacción de consumidores (NPS)",
                en: "Consumer Satisfaction (NPS)"
            },
            tooltip: {
                es: "Mide la satisfacción de los consumidores del 1 al 10 utilizando Net Promoter Score (NPS)",
                en: "Measures consumer satisfaction from 1 to 10 using Net Promoter Score (NPS)"
            }
        },
        positiveSentiment: {
            title: {
                es: "Sentimiento Positivo",
                en: "Positive Sentiment"
            },
            tooltip: {
                es: "Mide en porcentaje las respuestas categorizadas como positivas con AI, analizando solo texto de respuestas en Reviews y Encuestas.",
                en: "Measures the percentage of responses categorized as positive using AI, analyzing only text from Reviews and Surveys."
            }
        },
        trafficLight: {
            values: {
                good: {
                    es: "Bueno",
                    en: "Good"
                },
                regular: {
                    es: "Regular",
                    en: "Regular"
                },
                bad: {
                    es: "Malo",
                    en: "Bad"
                }
            },
            percentages: {
                engagement: {
                    bad: {
                        es: 'Menos de 3%',
                        en: 'Less than 3%'
                    },
                    regular: {
                        es: '3% - 5%',
                        en: '3% - 5%'
                    },
                    good: {
                        es: '5% en adelante',
                        en: '5% and above'
                    }
                },
                sentimentAndNps: {
                    bad: {
                        es: 'Menos de 65%',
                        en: 'Less than 65%'
                    },
                    regular: {
                        es: '65% - 85%',
                        en: '65% - 85%'
                    },
                    good: {
                        es: '85% en adelante',
                        en: '85% and above'
                    }
                }
            }
        }
    },
    noData: {
        es: "No hay datos",
        en: "No data"
    }
};

export default translate(GlobalMetrics);
